import { InlineFormattedNumber } from '@src/components/InlineFormattedNumber';
import { useBalanceByCurrency } from '@src/hooks/useBalanceByCurrency';
import { useMarketDataContext } from '@src/Providers/MarketDataContext';
import {
  type Currency,
  CurrencyIcon,
  primeSwapForm,
  useWLOrderFormDispatch,
  useWLOrderFormSelector,
} from '@talos/kyoko';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';

export function PortfolioItem({ currency }: { currency: Currency }) {
  const dispatch = useWLOrderFormDispatch();
  const navigate = useNavigate();
  const balanceByCurrency = useBalanceByCurrency();
  const { sparklineBySymbol } = useMarketDataContext();
  const favoredPairCurrency = useWLOrderFormSelector(state => state.swap.referenceData.favoredPairCurrency);

  const amt = balanceByCurrency?.get(currency.Symbol)?.Amount;
  const equivalent = balanceByCurrency?.get(currency.Symbol)?.Equivalent;
  const sparkline = sparklineBySymbol?.get(`${currency.Symbol}-${favoredPairCurrency?.Symbol}`);
  const percentChange = sparkline?.PercentChange?.toFixed(2);
  const isPositive = percentChange && Number(percentChange) >= 0;

  return (
    <div
      key={currency.Symbol}
      className="border-color-border flex flex-row items-center gap-2 border-t px-3 py-2 tracking-wide"
      onClick={() => {
        dispatch(primeSwapForm({ toCurrency: currency.Symbol }));
        navigate('swap');
      }}
      data-testid="portfolio-item"
    >
      <CurrencyIcon currency={currency.Symbol} size={40} />
      <div data-testid={`portfolio-item-${currency.Symbol}`}>
        <div>
          <h1 className="text-color-text font-bold" data-testid="symbol">
            {currency.Symbol}
          </h1>
          <h2 className="text-color-text-weaker text-sm font-semibold">{currency.Description}</h2>
        </div>
      </div>
      <div className="ml-auto flex flex-col items-end">
        <div className="flex flex-row gap-1">
          <h1 className="text-color-text font-bold">
            {amt && !equivalent?.Amount ? (
              <span>-</span>
            ) : (
              <InlineFormattedNumber
                data-testid="portfolio-item-value"
                number={equivalent?.Amount}
                currency={equivalent?.Currency}
              />
            )}
          </h1>

          {percentChange && (
            <div
              className={clsx(
                'flex w-full flex-row items-center justify-between text-xs font-semibold tracking-tight',
                isPositive ? 'text-color-text-success-strong' : 'text-color-text-error-strong'
              )}
            >
              (
              <div className="flex items-center gap-1">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="8"
                  height="8"
                  viewBox="0 0 8 8"
                  fill="none"
                  className={clsx(!isPositive && 'rotate-180')}
                >
                  <path
                    d="M4.66635 1.3352L7.7221 5.04628C8.31575 5.76725 7.8953 7 7.05575 7L0.944251 7C0.104695 7 -0.315754 5.76725 0.277902 5.04628L3.33365 1.33521C3.70166 0.888265 4.29834 0.888265 4.66635 1.3352Z"
                    fill="currentColor"
                  />
                </svg>
                {percentChange}%)
              </div>
            </div>
          )}
        </div>
        <h2 className="text-color-text-weaker text-sm">
          <InlineFormattedNumber number={amt} currency={currency.Symbol} showCurrency={false} />
        </h2>
      </div>
    </div>
  );
}
